import React from 'react';
import { Alert20Regular } from '@fluentui/react-icons';
import { useLocalizedStrings } from 'i18n';
import { useNotificationsDispatch, useNotificationSettingsSummary } from 'store/notifications/useNotificationSettings';
import { useWebView } from 'store/webView/useWebView';

import { INotificationSettingsSummaryProps, NotificationSettingsSummary } from '../NotificationSettingsSummary';

interface IPushNotificationSettingsSummaryProps
  extends Omit<
    INotificationSettingsSummaryProps,
    'notificationType' | 'toggleNotificationLabel' | 'notificationsEnabled' | 'onToggleNotification' | 'telemetryProps'
  > {}

export const PushNotificationSettingsSummary = (props: IPushNotificationSettingsSummaryProps) => {
  const { onDrillDown } = props;
  const { notificationSettingsSummary } = useNotificationSettingsSummary();
  const { updateSelfNotificationSettings } = useNotificationsDispatch();
  const { t } = useLocalizedStrings();
  const { isWebView } = useWebView();

  const onToggle = React.useCallback(
    (isEnabled: boolean) => {
      updateSelfNotificationSettings({ isEnabled, channel: 'Push' });
    },
    [updateSelfNotificationSettings]
  );

  const text = isWebView
    ? t('OfficeShellPanel.NotificationSettings.Sections.Push.Text_WebView')
    : t('OfficeShellPanel.NotificationSettings.Sections.Push.Text');
  const confirmTitle = t('OfficeShellPanel.NotificationSettings.Sections.Push.TurnOffConfirm.Title');
  const confirmSubtext = t('OfficeShellPanel.NotificationSettings.Sections.Push.TurnOffConfirm.Subtext');

  const notificationsEnabled = notificationSettingsSummary?.pushNotificationSetting.isEnabled ?? false;
  const toggleNotificationLabel = notificationsEnabled
    ? t('OfficeShellPanel.NotificationSettings.Sections.On')
    : t('OfficeShellPanel.NotificationSettings.Sections.Off');

  const enabledTopicShortNames = React.useMemo(() => {
    if (onDrillDown) {
      if (notificationSettingsSummary?.pushNotificationSetting.areAllTopicsEnabled) {
        return [t('OfficeShellPanel.NotificationSettings.Sections.AllNotifications')];
      }
      return notificationSettingsSummary?.pushNotificationSetting.enabledTopicShortNames;
    }
    return undefined;
  }, [
    notificationSettingsSummary?.pushNotificationSetting.areAllTopicsEnabled,
    notificationSettingsSummary?.pushNotificationSetting.enabledTopicShortNames,
    onDrillDown,
    t,
  ]);

  return (
    <NotificationSettingsSummary
      notificationType="Push"
      title={t('OfficeShellPanel.NotificationSettings.Sections.Push.Title')}
      iconComponent={Alert20Regular}
      text={text}
      toggleNotificationLabel={toggleNotificationLabel}
      toggleAriaLabel={t('OfficeShellPanel.NotificationSettings.Sections.Push.Title')}
      notificationsEnabled={notificationsEnabled}
      isToggleable={notificationSettingsSummary?.pushNotificationSetting.isToggleable}
      onToggleNotification={onToggle}
      turnOffConfirmStrings={{
        title: confirmTitle,
        subText: confirmSubtext,
      }}
      enabledTopicShortNames={enabledTopicShortNames}
      {...props}
    />
  );
};
